import { assertNever } from "./type";

enum FormatTimeType {
  AGO = "ago",
  UNTIL = "until",
}

/** TODO make this `formatTimeDifference` and then just pass in `Temporal.Now.zonedDateTimeISO()`. Gives us a free time diff printer as well.  */
const formatTimeDifferenceFromNow = (
  time: string | undefined,
  type: FormatTimeType,
): string => {
  if (!time) {
    return "";
  }

  const now = Temporal.Now.zonedDateTimeISO();
  const inputTime = Temporal.Instant.from(time).toZonedDateTimeISO(
    now.timeZoneId,
  );
  let duration;
  switch (type) {
    case FormatTimeType.AGO:
      duration = now.since(inputTime);
      break;
    case FormatTimeType.UNTIL:
      duration = now.until(inputTime);
      break;
    default:
      assertNever(type);
  }

  const minutes = Math.floor(duration.total({ unit: "minute" }));

  const today = now.toPlainDate();
  const inputDate = inputTime.toPlainDate();
  let daysDifference;
  switch (type) {
    case FormatTimeType.AGO:
      daysDifference = today.since(inputDate).total({ unit: "day" });
      break;
    case FormatTimeType.UNTIL:
      daysDifference = today.until(inputDate).total({ unit: "day" });
      break;
    default:
      assertNever(type);
  }

  // "Now" for times within a minute
  if (minutes < 1) {
    return "Now";
  }

  // "<Number of mintues>m" for times within 60 minutes
  if (minutes < 60) {
    return `${minutes}m`;
  }

  // "hh:mm AM/PM" for times within the current day
  if (daysDifference < 1) {
    return inputTime
      .toPlainTime()
      .toLocaleString("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
  }

  // "Yesterday" or "Tomorrow" for times just a day apart
  if (daysDifference === 1) {
    switch (type) {
      case FormatTimeType.AGO:
        return "Yesterday";
      case FormatTimeType.UNTIL:
        return "Tomorrow";
      default:
        assertNever(type);
    }
  }

  // Day of the week for times within a week
  if (daysDifference < 7) {
    return inputDate.toLocaleString("en-US", { weekday: "long" });
  }

  // "<Month name>, dd, YYYY" for times further away than a week
  return getDateString(inputDate);
};

export const formatTimeAgo = (time: string | undefined): string => {
  return formatTimeDifferenceFromNow(time, FormatTimeType.AGO);
};

export const formatTimeUntil = (time: string | undefined): string => {
  return formatTimeDifferenceFromNow(time, FormatTimeType.UNTIL);
};

/**
 * Formats time difference from now in a compact format.
 * Examples: "2min ago", "2hr ago", "2d ago", "2mo ago", "2y ago"
 */
export const formatTimeAgoCompact = (
  time: string | Date | undefined,
): string => {
  if (!time) {
    return "";
  }

  const now = Temporal.Now.zonedDateTimeISO();
  const timeStr = time instanceof Date ? time.toISOString() : time;
  const inputTime = Temporal.Instant.from(timeStr).toZonedDateTimeISO(
    now.timeZoneId,
  );
  const duration = now.since(inputTime);

  // Handle future dates
  if (duration.sign < 0) {
    return "Just now";
  }

  const minutes = Math.floor(duration.total({ unit: "minute" }));
  const hours = Math.floor(duration.total({ unit: "hour" }));
  const days = Math.floor(duration.total({ unit: "day" }));

  // Calculate months and years using relativeTo
  const months = Math.floor(
    duration.total({ unit: "month", relativeTo: inputTime }),
  );
  const years = Math.floor(
    duration.total({ unit: "year", relativeTo: inputTime }),
  );

  // Less than 1 minute
  if (minutes < 1) {
    return "Now";
  }

  // Less than 60 minutes: "Xmin ago"
  if (minutes < 60) {
    return `${minutes}min ago`;
  }

  // Less than 24 hours: "Xhr ago"
  if (hours < 24) {
    return `${hours}hr ago`;
  }

  // Less than 30 days: "Xd ago"
  if (days < 30) {
    return `${days}d ago`;
  }

  // Less than 12 months: "Xmo ago"
  if (months < 12) {
    return `${months}mo ago`;
  }

  // 12 months or more: "Xy ago"
  return `${years}y ago`;
};

/**
 * Formats a date string with a timezone, for the given locale if provided. If no timezone given, will behave like `getDateTimeString`.
 */
export const formatDateStringWithTimezone = (
  date: string | Date,
  timezone?: string,
): string => {
  if (!timezone) {
    return getDateTimeString(new Date(date));
  }

  const instant = Temporal.Instant.fromEpochMilliseconds(
    new Date(date).getTime(),
  );
  const zonedDateTime = instant.toZonedDateTimeISO(timezone).toPlainDateTime();
  return getDateTimeString(zonedDateTime);
};

type DateLike = Date | Temporal.PlainDate;
type DateTimeLike = Date | Temporal.PlainDateTime | Temporal.Instant;

export const getDaysSince = (dateInstant: Temporal.Instant): number => {
  const now = Temporal.Now.instant();
  return Math.floor(Math.abs(now.until(dateInstant).total({ unit: "day" })));
};

export function getDateString(
  date: DateLike,
  options?: Pick<Intl.DateTimeFormatOptions, "year" | "month" | "day"> & {
    ianaTimezone?: string;
    locale?: string;
  },
): string {
  let dateToFormat = date;
  if (options?.ianaTimezone) {
    if (date instanceof Date) {
      dateToFormat = Temporal.Instant.fromEpochMilliseconds(date.getTime())
        .toZonedDateTimeISO(options.ianaTimezone)
        .toPlainDate();
    } else {
      dateToFormat = date.toZonedDateTime(options.ianaTimezone).toPlainDate();
    }
  }

  const locale =
    options?.locale ??
    (process.env.NODE_ENV === "test" || typeof navigator === "undefined" // not defined in node (server-side rendering)
      ? "en-US"
      : navigator.language);

  return dateToFormat.toLocaleString(locale, {
    ...getPreferredDateTimeFormatOption(true, false),
    ...options,
  });
}

export function getDateTimeString(date: DateTimeLike): string {
  return date.toLocaleString(
    process.env.NODE_ENV === "test" || typeof navigator === "undefined" // not defined in node (server-side rendering)
      ? "en-US"
      : navigator.language,
    getPreferredDateTimeFormatOption(true, true),
  );
}

export function getPreferredDateTimeFormatOption(
  includeDate: boolean,
  includeTime: boolean,
): Intl.DateTimeFormatOptions {
  return {
    month: includeDate ? "short" : undefined,
    day: includeDate ? "numeric" : undefined,
    year: includeDate ? "numeric" : undefined,
    hour: includeTime ? "numeric" : undefined,
    minute: includeTime ? "numeric" : undefined,
    second: includeTime ? "numeric" : undefined,
    hour12: includeTime ? true : undefined,
  };
}

/**
 * Creates a Date object from a YYYY-MM-DD string without timezone conversion.
 * This avoids the issue where new Date("2024-01-15") is interpreted as UTC
 * and then converted to local time, potentially changing the date.
 */
export function dateFromLocalDateString(dateString: string): Date {
  const parts = dateString.split("-");
  if (parts.length !== 3) {
    throw new Error(
      `Invalid date format: expected YYYY-MM-DD, got "${dateString}"`,
    );
  }

  const [yearStr, monthStr, dayStr] = parts;

  if (!yearStr || !monthStr || !dayStr) {
    throw new Error(`Invalid date format: empty components in "${dateString}"`);
  }

  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    throw new Error(
      `Invalid date format: non-numeric components in "${dateString}"`,
    );
  }

  return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
}
